import React, { ReactElement } from 'react';

import { observer } from 'mobx-react';

import {
  CheckboxCell,
  GridTableCell,
  GridTableRow,
  UserInfoCell,
} from '../../../../shared/components/table-components';
import { CustomerContactLink } from '../../../../shared/components/buttons';
import { ScholasticCustomerInfo } from '../../../interfaces/scholastic-list/ScholasticCustomerInfo';
import { scholasticCustomersListStore } from '../../../stores/ScholasticCustomersListStore';
import { StatusCell } from './StatusCell/StatusCell';
import { getLinkForCustomerInfo } from './utils/getLinkForCustomerInfo';
import { getStartDayInfo } from './utils/getStartDay';
import {
  SCHOLASTIC_CUSTOMER_COLUMN,
  ScholasticCustomerColumn,
} from '../NgzScholasticCustomerList.settings';

type CustomerRowProps = {
  userInfo: ScholasticCustomerInfo;
  visibleColumns: ScholasticCustomerColumn[];
};

const CustomerRow = observer(({ userInfo, visibleColumns }: CustomerRowProps) => {
  const { selectMethod } = scholasticCustomersListStore;
  const { getIsSelected, selectItem } = selectMethod;

  const coachName = `${userInfo.coachFirstName} ${userInfo.coachLastName}`;

  const isChecked = getIsSelected(userInfo.id);
  const handleCheck = () => {
    selectItem(userInfo.id);
  };

  const link = getLinkForCustomerInfo(userInfo.id, userInfo.scholasticStatus);
  const startDayInfo = getStartDayInfo(userInfo.subscriptionStart);

  const renderColumnsInfo: Record<ScholasticCustomerColumn, ReactElement> = {
    [SCHOLASTIC_CUSTOMER_COLUMN.NAME]: (
      <CheckboxCell showCheckbox={true} onCheck={handleCheck} isChecked={isChecked}>
        <UserInfoCell
          firstName={userInfo.firstName}
          lastName={userInfo.lastName}
          email={userInfo.email}
          phone={userInfo.phone}
          href={link}
          dataTestId="scholastic-info-cell"
        />
      </CheckboxCell>
    ),
    [SCHOLASTIC_CUSTOMER_COLUMN.LOCATION]: (
      <GridTableCell dataTestId="location-cell">{userInfo.gymName}</GridTableCell>
    ),
    [SCHOLASTIC_CUSTOMER_COLUMN.AFFILIATE]: (
      <GridTableCell dataTestId="school-cell" className="private-info">
        {userInfo.school}
      </GridTableCell>
    ),
    [SCHOLASTIC_CUSTOMER_COLUMN.COACH]: (
      <GridTableCell dataTestId="coach-cell" className="private-info">
        {coachName}
      </GridTableCell>
    ),
    [SCHOLASTIC_CUSTOMER_COLUMN.START_DATE]: (
      <GridTableCell dataTestId="start-cell">{startDayInfo}</GridTableCell>
    ),
    [SCHOLASTIC_CUSTOMER_COLUMN.STATUS]: (
      <StatusCell status={userInfo.scholasticStatus} />
    ),
    [SCHOLASTIC_CUSTOMER_COLUMN.CUSTOM_FILTER]: <div />,
    [SCHOLASTIC_CUSTOMER_COLUMN.ACTIONS]: (
      <GridTableCell justify="end" dataTestId="contact-cell">
        <CustomerContactLink customerId={userInfo.id} resourceType="scholastic" />
      </GridTableCell>
    ),
  };

  return (
    <GridTableRow
      variant="row"
      highlight={userInfo.newScansAvailable}
      dataTestId="customer-row"
    >
      {visibleColumns.map((col) => (
        <React.Fragment key={col}>{renderColumnsInfo[col]}</React.Fragment>
      ))}
    </GridTableRow>
  );
});

export { CustomerRow };
